// @flow
import React, {PureComponent, type Node} from 'react';
import classNames from 'classnames';
import ScrollListener from '../ScrollListener/ScrollListener';

import './Sticky.less';

type StickyRenderProp = Node | (({isSticky: boolean}) => Node);

type StickyOrNotProps = {
    isSticky: boolean,
    children: StickyRenderProp
};

/**
 * PureComponent inner component to prevent wasteful renders except when the
 * sticky bit flips
 */
class StickyOrNot extends PureComponent<StickyOrNotProps> {
    render() {
        const {children, isSticky} = this.props;
        return typeof children === 'function' ? children({isSticky}) : children;
    }
}

type Props = {
    top: number,
    className: string,
    children: StickyRenderProp
};

const Sticky = ({top, className, children}: Props) => (
    <ScrollListener
        useWindowAsScrollParent
        className={classNames('Sticky', className)}
        style={{top}}
    >
        {(rect) => (
            <StickyOrNot isSticky={!!rect && rect.top <= top}>
                {children}
            </StickyOrNot>
        )}
    </ScrollListener>
);

export const StickyAsFixed = ({top, className, children}: Props) => (
    <ScrollListener
        useWindowAsScrollParent
        className="StickyAsFixedContainer"
        throttleAmount={50}
    >
        {(rect) => {
            let isSticky = !!rect && rect.top <= top;
            return (
                <div
                    style={{
                        height: !!rect && rect.height
                    }}
                >
                    <div
                        className={classNames('StickyAsFixed', className, {
                            [`${className}--fixed`]: isSticky,
                            [`${className}--relative`]: !isSticky
                        })}
                        style={{
                            position: isSticky ? 'fixed' : 'relative',
                            top
                        }}
                    >
                        <StickyOrNot isSticky={isSticky}>
                            {children}
                        </StickyOrNot>
                    </div>
                </div>
            );
        }}
    </ScrollListener>
);

export default Sticky;
